import React, { useState } from 'react';
import { Database, RefreshCw, TestTube } from 'lucide-react';
import { populateTables, forceRepopulateTables } from '../../utils/populateTables';
import { testFirebaseConnection } from '../../utils/testFirebase';
import { useData } from '../../context/DataContext';

const PopulateDataButton: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { refreshTables } = useData();

  const handlePopulate = async () => {
    setLoading(true);
    setMessage('Populating tables...');
    
    const result = await populateTables();
    
    if (result.success) {
      setMessage(`✅ ${result.message} (${result.count} tables)`);
      // Refresh tables from Firebase
      await refreshTables();
    } else {
      setMessage(`❌ Error: ${result.message}`);
    }
    
    setLoading(false);
    
    // Clear message after 5 seconds
    setTimeout(() => setMessage(''), 5000);
  };

  const handleForceRepopulate = async () => {
    if (!window.confirm('This will delete all existing tables and create fresh ones. Continue?')) {
      return;
    }

    setLoading(true);
    setMessage('Force repopulating tables...');
    
    const result = await forceRepopulateTables();
    
    if (result.success) {
      setMessage(`✅ ${result.message} (${result.count} tables)`);
      // Refresh tables from Firebase
      await refreshTables();
    } else {
      setMessage(`❌ Error: ${result.message}`);
    }
    
    setLoading(false);
  };

  const handleTestFirebase = async () => {
    setLoading(true);
    setMessage('Testing Firebase connection...');
    
    const result = await testFirebaseConnection();
    
    if (result.success) {
      setMessage(`✅ ${result.message}`);
    } else {
      setMessage(`❌ ${result.message}`);
    }
    
    setLoading(false);
    
    // Clear message after 8 seconds
    setTimeout(() => setMessage(''), 8000);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleTestFirebase}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <TestTube size={18} />
          {loading ? 'Testing...' : 'Test Firebase'}
        </button>

        <button
          onClick={handlePopulate}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Database size={18} />
          {loading ? 'Populating...' : 'Populate Tables'}
        </button>

        <button
          onClick={handleForceRepopulate}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw size={18} />
          Force Repopulate
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.includes('✅') 
            ? 'bg-green-500 bg-opacity-20 text-green-400 border border-green-500' 
            : 'bg-red-500 bg-opacity-20 text-red-400 border border-red-500'
        }`}>
          {message}
        </div>
      )}

      <div className="text-xs text-gray-400 space-y-1">
        <p>• <strong>Test Firebase:</strong> Tests connection and permissions (check browser console)</p>
        <p>• <strong>Populate Tables:</strong> Adds tables only if none exist</p>
        <p>• <strong>Force Repopulate:</strong> Deletes all existing tables and creates fresh ones</p>
      </div>
    </div>
  );
};

export default PopulateDataButton;
